import {
  ReportResponseDtoStatus,
  useReportsFindOne,
} from '@/api/WhisApi/generated';
import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import { LoadingView } from '@/components/LoadingView/LoadingView';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useOfflineDegradedMode } from '@/hooks/useOfflineDegradedMode';
import { useReferentialsData } from '@/hooks/useReferentialsData';
import { BottomButtonBar } from '@/screens/ReportCreation/components/BottomButtonBar';
import { FormRenderer } from '@/screens/ReportCreation/components/FormRenderer';
import { ReportCreationStepContent as ReportCreationStepRenderer } from '@/screens/ReportCreation/components/ReportCreationStepContent';
import { StepIndicator } from '@/screens/ReportCreation/components/StepIndicator/StepIndicator';
import { useReportEditPermissionGuard } from '@/screens/ReportCreation/hooks/useReportEditPermissionGuard';
import { useReportSubmission } from '@/screens/ReportCreation/hooks/useReportSubmission';
import { useReportSubmissionNavigation } from '@/screens/ReportCreation/hooks/useReportSubmissionNavigation';
import {
  ReportFormData,
  FormLayout,
  ReportCreationStep,
  ReportCreationStepContent,
} from '@/screens/ReportCreation/reportCreation.types';
import { Button } from '@components/Button/Button';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { getTokens, YStack } from 'tamagui';
import { getStep1FormConfig } from './forms/step1.config';
import { getStep2FormConfig } from './forms/step2.config';
import { getStep3FormConfig } from './forms/step3.config';
import { getStep4FormConfig } from './forms/step4.config';
import {
  buildEditFormDataFromReport,
  flattenFormLayoutSections,
  isFieldVisible,
  isValueFilled,
} from './reportCreation.utils';

import {
  buildDependencyValueMap,
  getConditionalDependencyNames,
} from '@/screens/ReportCreation/utils/conditionalVisibility.utils';
import { useVersionNoteStore } from '@/store/versionNote/versionNote.store';
import { useSimilarReportsCheck } from './hooks/useSimilarReportsCheck';

const defaultFormValues: Partial<ReportFormData> = {
  diagnosticProcedures: [{}],
};

/**
 * ReportCreationScreen - Multi-step form for creating reports
 *
 * Features:
 * - 4-step process: General Information, Surveillance and Etiology, Diagnostic Procedure, Supporting Documents
 * - Step indicator showing progress
 * - Navigation between steps with Back/Next buttons
 * - Save as draft functionality
 * - Responsive layout for mobile and web
 */
export default function ReportCreationScreen(): React.ReactNode {
  const { t, language } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const uiTokens = getTokens();
  const router = useRouter();
  const segments = useSegments() as string[];
  const { reportId, reset } = useLocalSearchParams<{
    reportId?: string;
    reset?: string;
  }>();
  const isEditMode = Boolean(reportId);
  const { user } = useAuth();
  const { isOfflineDegradedMode } = useOfflineDegradedMode();

  const { data: report, isPending: isLoadingReport } = useReportsFindOne(
    reportId ?? '',
    {
      query: { enabled: isEditMode && !isOfflineDegradedMode },
    },
  );

  useReportEditPermissionGuard({
    isEditMode,
    reportId,
    report,
    user,
  });

  const [currentStep, setCurrentStep] = useState<ReportCreationStep>(
    ReportCreationStep.generalInformation,
  );
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    // Go up each time current step change
    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
  }, [currentStep]);

  const { isSubmitting, submitReport } = useReportSubmission({
    reportCode: report?.code,
  });
  const setVersionNoteState = useVersionNoteStore((store) => store.setState);

  const isBoardRoute = segments.includes('board');

  const formMethods = useForm<ReportFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const editDefaultValues = useMemo(
    () => (report ? buildEditFormDataFromReport(report) : undefined),
    [report],
  );

  // Prevent reapplying edit defaults on refetch/re-render, which would overwrite user edits.
  const lastPrefilledReportIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editDefaultValues || !report) {
      return;
    }

    if (lastPrefilledReportIdRef.current === report.id) {
      return;
    }

    formMethods.reset({ ...defaultFormValues, ...editDefaultValues });
    lastPrefilledReportIdRef.current = report.id;
  }, [editDefaultValues, formMethods, report]);

  const diagnosticProcedureValue = formMethods.watch('diagnosticProcedure');
  const showDiagnosticStep = diagnosticProcedureValue === 'yes';
  const diagnosticProcedures = formMethods.watch('diagnosticProcedures');

  const screenTitle = isEditMode
    ? t('reportCreation.editTitle')
    : t('reportCreation.title');

  const steps: ReportCreationStepContent[] = useMemo(
    () => [
      { title: t('reportCreation.steps.generalInformation') },
      { title: t('reportCreation.steps.surveillanceAndEtiology') },
      { title: t('reportCreation.steps.diagnosticProcedure') },
      { title: t('reportCreation.steps.supportingDocuments') },
    ],
    [t],
  );

  const totalSteps = steps.length;

  const step1FormConfig = useMemo(
    () => getStep1FormConfig(t, language),
    [t, language],
  );
  const { data: referentials } = useReferentialsData();

  const step2FormConfig = useMemo(
    () => getStep2FormConfig(t, language, referentials),
    [t, language, referentials],
  );

  const step3FormConfig = useMemo(
    () => getStep3FormConfig(t, language, referentials),
    [t, language, referentials],
  );
  const step4FormConfig = useMemo(
    () => getStep4FormConfig(t, language),
    [t, language],
  );

  const shouldShowMissingIndicators =
    isEditMode && report?.status === ReportResponseDtoStatus.draft;
  const isEditingPublishedReport =
    isEditMode &&
    (report?.status === ReportResponseDtoStatus.published ||
      report?.status === ReportResponseDtoStatus.pendingRRA ||
      report?.status === ReportResponseDtoStatus.publishedRRA);

  const repeatableCounts = useMemo(
    () => ({
      diagnosticProcedures: Array.isArray(diagnosticProcedures)
        ? diagnosticProcedures.length
        : 0,
    }),
    [diagnosticProcedures],
  );

  const currentFormLayout = useMemo<FormLayout>(() => {
    switch (currentStep) {
      case ReportCreationStep.generalInformation:
        return step1FormConfig;
      case ReportCreationStep.surveillanceAndEtiology:
        return step2FormConfig;
      case ReportCreationStep.diagnosticProcedure:
        return showDiagnosticStep ? step3FormConfig : { sections: [] };
      case ReportCreationStep.supportingDocuments:
        return step4FormConfig;
      default:
        return { sections: [] };
    }
  }, [
    currentStep,
    showDiagnosticStep,
    step1FormConfig,
    step2FormConfig,
    step3FormConfig,
    step4FormConfig,
  ]);

  // Get required field IDs based on current step
  const currentFormSections = useMemo(
    () => flattenFormLayoutSections(currentFormLayout, repeatableCounts),
    [currentFormLayout, repeatableCounts],
  );

  const dependencyNames = useMemo(
    () => getConditionalDependencyNames(currentFormSections),
    [currentFormSections],
  );

  const dependencyValues = formMethods.watch(dependencyNames);

  const dependencyValueMap = useMemo(
    () => buildDependencyValueMap(dependencyNames, dependencyValues ?? []),
    [dependencyNames, dependencyValues],
  );

  const visibleRequiredFieldIds = useMemo(() => {
    return currentFormSections.reduce<string[]>((acc, section) => {
      section.fields.forEach((field) => {
        if (!field.required) {
          return;
        }

        if (!isFieldVisible(field, dependencyValueMap)) {
          return;
        }

        if (field.type === 'location') {
          acc.push(`${field.id}.latitude`, `${field.id}.longitude`);
          return;
        }

        acc.push(field.id);
      });

      return acc;
    }, []);
  }, [currentFormSections, dependencyValueMap]);

  const watchedRequiredFieldValues = formMethods.watch(visibleRequiredFieldIds);

  const hasRequiredValues = useMemo(
    () =>
      visibleRequiredFieldIds.every((_, index) =>
        isValueFilled(watchedRequiredFieldValues?.[index]),
      ),
    [visibleRequiredFieldIds, watchedRequiredFieldValues],
  );

  const handleBack = useCallback((): void => {
    setCurrentStep((prev) => {
      if (
        prev === ReportCreationStep.supportingDocuments &&
        !showDiagnosticStep
      ) {
        return ReportCreationStep.surveillanceAndEtiology;
      }

      return Math.max(
        ReportCreationStep.generalInformation,
        prev - 1,
      ) as ReportCreationStep;
    });
  }, [showDiagnosticStep]);

  const handleNext = (): void => {
    formMethods.handleSubmit(() => {
      setCurrentStep((prev) => {
        if (
          prev === ReportCreationStep.surveillanceAndEtiology &&
          !showDiagnosticStep
        ) {
          return Math.min(totalSteps, prev + 2) as ReportCreationStep;
        }
        return Math.min(totalSteps, prev + 1) as ReportCreationStep;
      });
    })();
  };

  const resetReportForm = useCallback((): void => {
    formMethods.reset(defaultFormValues);
    setCurrentStep(ReportCreationStep.generalInformation);
  }, [formMethods]);

  const { handleSubmitError, handleSubmissionSuccess, handleQueuedSuccess } =
    useReportSubmissionNavigation({
      isBoardRoute,
      isEditContext: isEditMode && Boolean(reportId),
      editedReportId: reportId,
      onBeforeNavigate: resetReportForm,
    });

  useEffect(() => {
    if (reset !== 'true' || isEditMode) {
      return;
    }

    resetReportForm();
    router.replace('/(tabs)/report-creation');
  }, [isEditMode, reset, resetReportForm, router]);

  const submitPublish = useCallback(
    async (values: ReportFormData): Promise<void> => {
      await submitReport(values, 'published', {
        onSuccess: (createdReport): void => {
          handleSubmissionSuccess('published', createdReport.id);
        },
        onQueued: handleQueuedSuccess,
        onError: handleSubmitError,
      });
    },
    [
      handleQueuedSuccess,
      handleSubmissionSuccess,
      handleSubmitError,
      submitReport,
    ],
  );

  const { isCheckingSimilarReports, triggerSimilarReportsCheck } =
    useSimilarReportsCheck({
      reportCode: report?.code,
      reportId,
      report,
      onPublish: submitPublish,
    });

  const handlePublish = (): void => {
    formMethods.handleSubmit((values) => {
      triggerSimilarReportsCheck(values);
    })();
  };

  const handleGoToVersionNote = (): void => {
    formMethods.handleSubmit((values) => {
      if (!reportId) {
        return;
      }

      setVersionNoteState({
        pendingValues: values,
        reportCode: report?.code,
        report,
      });
      router.push({
        pathname: isBoardRoute
          ? '/(tabs)/board/reports/[reportId]/edit/version-note'
          : '/(tabs)/reports/[reportId]/edit/version-note',
        params: { reportId },
      });
    })();
  };

  const handleSaveAsDraft = useCallback((): void => {
    const values = formMethods.getValues();
    void submitReport(values, 'draft', {
      onSuccess: (createdReport): void => {
        handleSubmissionSuccess('draft', createdReport.id);
      },
      onQueued: handleQueuedSuccess,
      onError: handleSubmitError,
    });
  }, [
    formMethods,
    handleSubmissionSuccess,
    handleQueuedSuccess,
    handleSubmitError,
    submitReport,
  ]);

  const handleBackToReport = useCallback((): void => {
    if (!reportId) {
      return;
    }

    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace({
      pathname: '/(tabs)/reports/[reportId]',
      params: { reportId },
    });
  }, [reportId, router]);

  let primaryButtonLabel = t('reportCreation.buttons.next');
  let primaryButtonAction = handleNext;

  if (currentStep === totalSteps) {
    if (isEditingPublishedReport) {
      primaryButtonLabel = t('reportCreation.buttons.next');
      primaryButtonAction = handleGoToVersionNote;
    } else {
      primaryButtonLabel = t('reportCreation.buttons.publish');
      primaryButtonAction = handlePublish;
    }
  }

  // Disable Next/Save button if current step has required fields that are not filled
  const isFormValidationStep =
    currentStep === ReportCreationStep.generalInformation ||
    currentStep === ReportCreationStep.surveillanceAndEtiology ||
    (currentStep === ReportCreationStep.diagnosticProcedure &&
      showDiagnosticStep);
  const primaryButtonDisabled = isFormValidationStep && !hasRequiredValues;
  const primaryButtonLoading = isSubmitting || isCheckingSimilarReports;

  useEffect(() => {
    if (
      !showDiagnosticStep &&
      currentStep === ReportCreationStep.diagnosticProcedure
    ) {
      setCurrentStep(ReportCreationStep.supportingDocuments);
    }
  }, [showDiagnosticStep, currentStep]);

  const renderFormLayout = (
    layout: FormLayout,
    content?: React.ReactNode,
  ): React.ReactNode => (
    <FormProvider
      control={formMethods.control}
      register={formMethods.register}
      unregister={formMethods.unregister}
      handleSubmit={formMethods.handleSubmit}
      watch={formMethods.watch}
      setValue={formMethods.setValue}
      getValues={formMethods.getValues}
      reset={formMethods.reset}
      resetField={formMethods.resetField}
      clearErrors={formMethods.clearErrors}
      setError={formMethods.setError}
      setFocus={formMethods.setFocus}
      trigger={formMethods.trigger}
      formState={formMethods.formState}
      getFieldState={formMethods.getFieldState}
      subscribe={formMethods.subscribe}
    >
      {content ?? (
        <FormRenderer
          sections={layout.sections}
          repeatableSections={layout.repeatableSections}
        />
      )}
    </FormProvider>
  );

  const renderStepContent = (): React.ReactNode => {
    const diagnosticLayout = showDiagnosticStep
      ? step3FormConfig
      : { sections: [] };

    switch (currentStep) {
      case ReportCreationStep.generalInformation:
        return renderFormLayout(
          step1FormConfig,
          <ReportCreationStepRenderer
            layout={step1FormConfig}
            showMissingIndicator={shouldShowMissingIndicators}
            indicatorLabel={t('reportDetail.missingIndicator')}
          />,
        );
      case ReportCreationStep.surveillanceAndEtiology:
        return renderFormLayout(
          step2FormConfig,
          <ReportCreationStepRenderer
            layout={step2FormConfig}
            showMissingIndicator={shouldShowMissingIndicators}
            indicatorLabel={t('reportDetail.missingIndicator')}
          />,
        );
      case ReportCreationStep.diagnosticProcedure:
        return renderFormLayout(
          diagnosticLayout,
          <ReportCreationStepRenderer
            layout={diagnosticLayout}
            showMissingIndicator={shouldShowMissingIndicators}
            indicatorLabel={t('reportDetail.missingIndicator')}
          />,
        );
      case ReportCreationStep.supportingDocuments:
        return renderFormLayout(step4FormConfig);
      default:
        return null;
    }
  };

  if (isEditMode && isLoadingReport) {
    return (
      <TabScreenContainer>
        <LoadingView />
      </TabScreenContainer>
    );
  }

  if (isEditMode && !report) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      <YStack flex={1}>
        {!isSmallScreen && (
          <YStack marginBottom="$true">
            <ScreenHeader
              title={screenTitle}
              actions={
                isEditMode && reportId ? (
                  <Button
                    variant="outlined"
                    title={t('reportCreation.backToReport')}
                    onPress={handleBackToReport}
                  />
                ) : null
              }
            />
          </YStack>
        )}

        <StepIndicator
          totalSteps={totalSteps}
          currentStep={currentStep}
          stepTitles={steps.map((step) => step.title)}
        />

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <ScrollView
            ref={scrollViewRef}
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              flexGrow: 1,
              paddingVertical: uiTokens.space.$true.val,
            }}
          >
            {renderStepContent()}
          </ScrollView>
        </KeyboardAvoidingView>

        <BottomButtonBar
          showBackButton={currentStep > ReportCreationStep.generalInformation}
          onBack={handleBack}
          showSaveAsDraft={!isEditingPublishedReport}
          onSaveAsDraft={handleSaveAsDraft}
          primaryButtonLabel={primaryButtonLabel}
          onPrimaryAction={primaryButtonAction}
          primaryButtonDisabled={primaryButtonDisabled}
          primaryButtonLoading={primaryButtonLoading}
        />
      </YStack>
    </TabScreenContainer>
  );
}
